import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthApiService } from './auth.api.service';
import { ThemeService } from './theme.services';

type JwtPayload = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class AuthService {
	private api = inject(AuthApiService);
	private router = inject(Router);
	private themeService = inject(ThemeService);

	readonly token = signal<string | null>(this.readToken());
	readonly claims = signal<JwtPayload>(this.parseJwtPayload(this.token() ?? ''));

	readonly isMaster = computed(() => {
		const c = this.claims();
		const role = String(c['role'] ?? c['userRole'] ?? c['user_role'] ?? '').toLowerCase();
		return role === 'master';
	});

	readonly isVet = computed(() => {
		const c = this.claims();
		const role = String(c['role'] ?? c['userRole'] ?? c['user_role'] ?? '').toLowerCase();
		return role === 'vet';
	});

	readonly userId = computed(() => {
		const c = this.claims();
		return String(c['sub'] ?? c['userId'] ?? c['user_id'] ?? '').trim();
	});

	readonly preferredName = computed(() => {
		const c = this.claims();

		const v =
			c['preferredName'] ??
			c['preferred_name'] ??
			c['name'] ??
			c['fullName'] ??
			c['full_name'] ??
			'';
		return String(v || '').trim();
	});

	setToken(t: string) {
		localStorage.setItem('authToken', t);
		this.token.set(t);
		this.claims.set(this.parseJwtPayload(t));
	}

	getToken(): string | null {
		return this.token() ?? this.readToken();
	}

	clearToken() {
		localStorage.removeItem('authToken');
		sessionStorage.removeItem('authToken');
		this.token.set(null);
		this.claims.set({});
	}

	clearSession() {
		this.clearToken();
	}

	async logout() {
		try {
			await firstValueFrom(this.api.logout());
		} catch {
			return;
		}

		this.themeService.cleanUserColor();
		this.clearSession();
		this.router.navigateByUrl('/auth/login');
	}

	private readToken(): string | null {
		return localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
	}

	private parseJwtPayload(token: string): JwtPayload {
		if (!token) return {};
		const parts = token.split('.');
		if (parts.length < 2) return {};

		const payload = parts[1] ?? '';
		const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
		const padded = normalized.padEnd(
			normalized.length + ((4 - (normalized.length % 4)) % 4),
			'='
		);

		try {
			const json = decodeURIComponent(
				Array.from(atob(padded))
					.map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
					.join('')
			);
			const parsed = JSON.parse(json);
			return parsed && typeof parsed === 'object' ? (parsed as JwtPayload) : {};
		} catch {
			return {};
		}
	}
}
